import api, { isSessionExpiredError, type ApiClientError } from './api';
import type {
  BackupFile,
  RecycleBinFile,
  FileSearchParams,
  PaginationInfo,
  PreCheckRequest,
  PreCheckResponse,
} from '../types/file.types';
import type { ApiResponse } from '../types/api.types';
import { directUpload, type ChunkedUploadProgress, type DirectUploadProgress, type UploadPause } from './direct-upload.service';
import { beginUploadActivity } from '../stores/uploadActivityStore';
import {
  downloadFileStreaming,
  LARGE_FILE_THRESHOLD_BYTES,
  isFileSystemAccessSupported,
} from './streaming-download.service';

// Threshold for using chunked upload (50MB)
const CHUNKED_UPLOAD_THRESHOLD = 50 * 1024 * 1024;

/**
 * Whether uploadFile() takes the presigned chunked path (ADR-011) for this
 * file, as opposed to the single-shot POST /files/upload. Only the chunked
 * path has a server-side session, so only it can be aborted.
 */
export function usesChunkedUpload(file: File, forceChunked = false): boolean {
  return forceChunked || file.size > CHUNKED_UPLOAD_THRESHOLD;
}

// Backend single-file cap (backend/internal/files/constants.go MaxFileSize = 256 GiB).
// Enforced client-side before hashing so an oversized file is rejected immediately.
export const MAX_FILE_SIZE_BYTES = 256 * 1024 ** 3;

// Blocked upload extensions (QA #10 / decision D2). This mirrors the backend
// default (backend/pkg/config UPLOAD_BLOCKED_EXTENSIONS) as a UX hint only —
// the backend's upload/init and legacy upload enforce the real rule, so this
// list drifting behind the server just means one avoidable round-trip, never
// a bypass.
export const BLOCKED_UPLOAD_EXTENSIONS = [
  'exe', 'bat', 'cmd', 'com', 'cpl', 'scr', 'pif', 'msi', 'msp', 'dll', 'sys',
  'ps1', 'psm1', 'vbs', 'vbe', 'js', 'jse', 'wsf', 'wsh', 'hta', 'jar', 'reg',
  'lnk', 'sh', 'bash', 'run', 'deb', 'rpm', 'apk', 'app', 'dmg',
];

// blockedUploadExtension returns the offending extension if fileName ends in a
// blocked one (case-insensitive, final extension only), else null. Pass the
// deployment's list from GET /auth/config (QA #74) when you have it; the
// built-in default is only the fallback before that request answers.
export function blockedUploadExtension(
  fileName: string,
  blocked: readonly string[] = BLOCKED_UPLOAD_EXTENSIONS,
): string | null {
  const dot = fileName.lastIndexOf('.');
  if (dot < 0 || dot === fileName.length - 1) return null;
  const ext = fileName.slice(dot + 1).toLowerCase();
  return blocked.includes(ext) ? ext : null;
}

// QA #102: pages may not import api directly (no-restricted-imports); the
// upload dialog asks through here whether a failure was the login session.
export { isSessionExpiredError };

const emptyPagination = (): PaginationInfo => ({ page: 1, limit: 0, total: 0, total_pages: 0 });

// QA #93: an upload that fails leaves a failed row in the file list. The
// report goes to the backend at once; when the browser is offline (the usual
// reason the upload failed) it waits in localStorage and is resent when the
// connection returns or the file list is next opened.
export interface UploadFailureReport {
  file_hash: string;
  file_name: string;
  file_size: number;
}

const PENDING_UPLOAD_FAILURES_KEY = 'sentrax.pendingUploadFailures';

function readPendingFailures(): UploadFailureReport[] {
  try {
    const raw = localStorage.getItem(PENDING_UPLOAD_FAILURES_KEY);
    const list = raw ? (JSON.parse(raw) as UploadFailureReport[]) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function writePendingFailures(list: UploadFailureReport[]): void {
  try {
    if (list.length === 0) localStorage.removeItem(PENDING_UPLOAD_FAILURES_KEY);
    else localStorage.setItem(PENDING_UPLOAD_FAILURES_KEY, JSON.stringify(list));
  } catch {
    /* storage unavailable — the backend's idle sweeper still records chunked uploads */
  }
}

/**
 * Whether a report that failed to send is worth keeping for a later resend:
 * the request never got an answer (offline, DNS, reset), or (QA #102) the
 * login session had ended — the report goes through after the next login.
 * Any other answer (400, 500…) is a refusal that resending would not change.
 * The api interceptor rejects with an ApiClientError (status, no `response`);
 * a raw axios error is handled too.
 */
function worthRequeue(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  if (isSessionExpiredError(err)) return true;
  const e = err as ApiClientError & { response?: { status?: number } };
  const original = e.originalError as { code?: string } | undefined;
  if (e.code === 'ERR_CANCELED' || original?.code === 'ERR_CANCELED') return false;
  return e.status === undefined && e.response?.status === undefined;
}

function queueUploadFailure(report: UploadFailureReport): void {
  const pending = readPendingFailures().filter((p) => p.file_hash !== report.file_hash);
  writePendingFailures([...pending, report]);
}

async function postUploadFailure(report: UploadFailureReport): Promise<void> {
  await api.post('/api/v1/files/upload/failed', report, { skipGlobalError: true });
}

export const fileService = {
  /**
   * Records a failed upload in the file list (QA #93). Never throws: when the
   * backend cannot be reached, or the login session has ended, the report is
   * kept for flushUploadFailures. With `sessionExpired` (QA #102) it is kept
   * straight away — the POST could only 401, and a single-shot upload's 401
   * sends the page to /login a second later.
   */
  async reportUploadFailure(report: UploadFailureReport, opts?: { sessionExpired?: boolean }): Promise<void> {
    if (opts?.sessionExpired) {
      queueUploadFailure(report);
      return;
    }
    try {
      await postUploadFailure(report);
    } catch (err) {
      if (worthRequeue(err)) queueUploadFailure(report); // else refused — resending would not help
    }
  },

  /**
   * Resends the reports kept while offline or logged out; returns how many
   * went through. Called on the file list, on `online` and after login.
   */
  async flushUploadFailures(): Promise<number> {
    const pending = readPendingFailures();
    if (pending.length === 0) return 0;
    const left: UploadFailureReport[] = [];
    let sent = 0;
    for (const report of pending) {
      try {
        await postUploadFailure(report);
        sent++;
      } catch (err) {
        if (worthRequeue(err)) left.push(report);
      }
    }
    writePendingFailures(left);
    return sent;
  },

  // 獲取備份檔案列表
  // Cancel an in-flight presigned upload on the server (QA #9): closes the
  // session and purges its chunk objects. Best-effort — a 404 just means the
  // session had already completed or expired.
  async abortUpload(fileHash: string): Promise<void> {
    await api.post('/api/v1/files/upload/abort', { file_hash: fileHash }, { skipGlobalError: true });
  },

  async getFiles(params?: FileSearchParams): Promise<{ files: BackupFile[]; pagination: PaginationInfo }> {
    const response = await api.get<ApiResponse<{ files: BackupFile[]; pagination: PaginationInfo }>>(
      '/api/v1/files',
      { params }
    );
    return response.data.data || { files: [], pagination: emptyPagination() };
  },

  /**
   * Files whose cloud shards can no longer be reconstructed (status=corrupted,
   * ADR-022). Backend GET /api/v1/files/corrupted — every role, scoped like
   * the file list (admin all, sub_admin group, operator own; QA #44), capped
   * at 1000 rows (A-05, plan P-6).
   */
  async listCorruptedFiles(): Promise<{ files: BackupFile[]; count: number }> {
    const response = await api.get<ApiResponse<{ files: BackupFile[]; count: number }>>(
      '/api/v1/files/corrupted'
    );
    return response.data.data || { files: [], count: 0 };
  },

  // 下載檔案（瀏覽器下載）。
  // ADR-044: 不再先探測 agent 的 LAN 位址再退回瀏覽器下載——還原到 watcher
  // 主機走 restore.service.ts 的還原請求（Restore 頁）。
  //
  // INFRA-ADR-023 D5 / SRS-008 FR-3:
  //   fileSize ≥ 2GB 走 streaming-download.service(showSaveFilePicker + ReadableStream)
  //   fileSize <  2GB 維持 blob 路徑(小檔快速體驗)
  //   不支援 FS Access API 的 browser(Safari) → 回 'unsupported',caller 顯示提示
  async downloadFile(
    fileId: string,
    fileName: string,
    fileSize?: number,
  ): Promise<{ method: 'browser' | 'fs-access' | 'unsupported' }> {
    // Large file + FS Access API 支援 → streaming path(避免 browser blob OOM)
    if (fileSize !== undefined && fileSize >= LARGE_FILE_THRESHOLD_BYTES) {
      if (!isFileSystemAccessSupported()) {
        // 沒裝 agent 也沒 FS Access API → caller 需顯示提示
        return { method: 'unsupported' };
      }
      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      // Same origin as the axios instance (api.ts): VITE_API_ENDPOINT; empty = relative → nginx proxy
      const baseUrl = import.meta.env.VITE_API_ENDPOINT || window.location.origin;
      const result = await downloadFileStreaming(baseUrl, {
        fileId,
        fileName,
        fileSize,
        token,
      });
      return { method: result.method === 'fs-access' ? 'fs-access' : 'browser' };
    }

    // Small file:走既有 blob 路徑
    // timeout: 0 — the instance-wide 30s timeout (api.ts) bounds the whole
    // transfer, not just the wait for a response: a 500MB file on a client
    // doing ~1.5MB/s was cut at 30s every time (prod, 2026-10-20).
    const response = await api.post(
      `/api/v1/files/${fileId}/download`,
      {},
      { responseType: 'blob', timeout: 0 }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { method: 'browser' };
  },

  // 刪除檔案
  async deleteFile(fileId: string): Promise<void> {
    await api.delete(`/api/v1/files/${fileId}`);
  },

  // 獲取回收站列表 (server-side pagination; backend ListOptions default limit=20)
  async getRecycleBin(
    params?: Pick<FileSearchParams, 'page' | 'limit'>,
  ): Promise<{ files: RecycleBinFile[]; pagination: PaginationInfo; retentionDays: number | null }> {
    const response = await api.get<
      ApiResponse<{ files: BackupFile[]; pagination: PaginationInfo; retention_days?: number }>
    >('/api/v1/files/recycle-bin', { params });
    const files = response.data.data?.files || [];
    const pagination = response.data.data?.pagination || emptyPagination();

    // Remaining days come from the server (its clock, the purge job's retention).
    // Computing them here used the browser clock: a browser a few seconds behind
    // the API showed 16 days for a file deleted a moment ago.
    const recycleFiles = files.map((file) => ({
      id: file.id,
      name: file.file_name || file.name || '',
      path: file.file_path || file.path || '',
      deletedTime: file.deleted_at ? new Date(file.deleted_at).toLocaleString('zh-TW') : '',
      remainingDays: file.days_until_permanent_delete ?? null,
    }));

    return { files: recycleFiles, pagination, retentionDays: response.data.data?.retention_days ?? null };
  },

  // 從回收站還原
  async restoreFromRecycle(fileId: string): Promise<void> {
    await api.post(`/api/v1/files/recycle-bin/${fileId}/restore`);
  },

  // 強制刪除（從回收站永久刪除）
  async forceDelete(fileId: string): Promise<void> {
    await api.delete(`/api/v1/files/recycle-bin/${fileId}`);
  },

  // 清空回收站
  async emptyRecycleBin(): Promise<void> {
    await api.delete('/api/v1/files/recycle-bin', {
      timeout: 5 * 60 * 1000, // 5 minutes for large recycle bin
    });
  },

  // 清除所有失敗的檔案記錄
  async clearErrorFiles(): Promise<{ count: number; skipped: number }> {
    const response = await api.delete<ApiResponse<{ count: number; skipped?: number; message: string }>>('/api/v1/files/error');
    return { count: response.data.data?.count || 0, skipped: response.data.data?.skipped || 0 };
  },

  // 預檢查檔案（重複檔案）
  async preCheckFile(request: PreCheckRequest): Promise<PreCheckResponse> {

    const response = await api.post<ApiResponse<PreCheckResponse>>(
      '/api/v1/files/pre-check',
      request
    );
    return response.data.data!;
  },

  // 上傳檔案（支援 hash 驗證和儲存複用）
  // 大檔案 (>50MB) 自動使用 chunked upload
  async uploadFile(
    file: File,
    options?: {
      fileHash?: string;
      reuseStorage?: boolean;
      /** percent 0–100, plus the measured upload speed in bytes/s once known (QA #70) */
      onProgress?: (progress: number, speedBytesPerSec?: number) => void;
      onChunkedProgress?: (progress: ChunkedUploadProgress) => void;
      forceChunked?: boolean; // Force chunked upload regardless of size
      signal?: AbortSignal; // For cancellation
      /** ADR-054: UPLOAD_RESUME_WINDOW in ms (GET /auth/config); chunked uploads wait out outages this long. */
      resumeWindowMs?: number;
      /** ADR-054: the chunked upload paused for the network (pause) or resumed (null). */
      onPauseChange?: (pause: UploadPause | null) => void;
    }
  ): Promise<{ file_id: string; file_hash: string; status: string }> {
    const {
      fileHash,
      reuseStorage = false,
      onProgress,
      onChunkedProgress,
      forceChunked = false,
      signal,
      resumeWindowMs,
      onPauseChange,
    } = options || {};

    // Validate file before upload
    if (!file) {
      throw new Error('檔案物件無效');
    }
    if (file.size === 0) {
      throw new Error('檔案大小為 0，無法上傳');
    }
    if (!file.name) {
      throw new Error('檔案名稱無效');
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      throw new Error('檔案大小超過 256 GiB 上限，無法上傳');
    }

    // Log file info for debugging
    console.log('[Upload] File info:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      useChunked: usesChunkedUpload(file, forceChunked),
    });

    // Use presigned URL direct upload for large files (ADR-011)
    if (usesChunkedUpload(file, forceChunked)) {
      console.log('[Upload] Using presigned URL direct upload for large file');

      const result = await directUpload(file, {
        onProgress: (progress: DirectUploadProgress) => {
          // Map DirectUploadProgress to ChunkedUploadProgress for compatibility
          if (onChunkedProgress) {
            const mappedPhase = progress.phase === 'processing' || progress.phase === 'completed' || progress.phase === 'failed'
              ? 'completing' : progress.phase;
            onChunkedProgress({
              phase: mappedPhase,
              overallProgress: progress.overallProgress,
              speed: progress.uploadProgress?.speed ?? 0,
              eta: progress.uploadProgress?.eta,
            } as ChunkedUploadProgress);
          }
          if (onProgress) {
            onProgress(progress.overallProgress, progress.uploadProgress?.speed);
          }
        },
        signal,
        resumeWindowMs,
        onPauseChange,
      });

      return {
        file_id: result.fileHash,
        file_hash: result.fileHash,
        status: result.status,
      };
    }

    // Use traditional single upload for smaller files
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('file_name', file.name);
    if (fileHash) {
      formData.append('file_hash', fileHash);
    }
    if (reuseStorage) {
      formData.append('reuse_storage', 'true');
    }

    // Debug: verify FormData contents
    const formDataFile = formData.get('file') as File;
    console.log('[Upload] FormData file:', {
      name: formDataFile?.name,
      size: formDataFile?.size,
      type: formDataFile?.type,
    });

    // Legacy handler responds with FileResponse (json key `id`, not `file_id`)
    const startedAt = Date.now();
    // ADR-054: an upload in progress is activity for the idle timer (the
    // chunked path marks itself in directUpload). No pause/resume here: a
    // single-shot POST has no server-side session to resume.
    const endUploadActivity = beginUploadActivity();
    const response = await api.post<ApiResponse<{ id: string; file_hash: string; status: string }>>(
      '/api/v1/files/upload',
      formData,
      {
        // Don't set Content-Type header manually - axios will set it automatically
        // with the correct boundary when using FormData
        timeout: 30 * 60 * 1000, // 30 minutes for large file uploads
        signal, // Pass abort signal
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            const elapsed = (Date.now() - startedAt) / 1000;
            onProgress(percentCompleted, elapsed > 0 ? progressEvent.loaded / elapsed : undefined);
          }
        },
      }
    ).finally(endUploadActivity);
    // Normalise to the same { file_id } shape the presigned path returns
    const data = response.data.data!;
    return { file_id: data.id, file_hash: data.file_hash, status: data.status };
  },

};
